<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";

const textbookAlignedPoints = [
  "Poem me speaker apne father ki un sacrifices ko yaad karta hai jo bachpan me us ne properly appreciate nahi kiye thay.",
  "Robert Hayden fatherly love ko words ke bajaye actions ke through show karte hain, is liye poem ka emotional effect quiet but powerful hai.",
  "Exam prep me central idea, tone, imagery aur the idea of neglected love sab se important points hote hain.",
];

const chapterCoverage = [
  "Father's actions",
  "Silent parental love",
  "Speaker's regret",
  "Tone and imagery",
  "Love and duty",
  "Delayed understanding",
];

const quickRevisionPoints = [
  "Father Sunday ko bhi early uth kar family ke liye warmth create karta hai.",
  "Poem me love actions ke through show hota hai, words ke through nahi.",
  "Speaker later regret feel karta hai.",
  "Tone reflective aur regretful hai.",
  "Theme unrecognized parental sacrifice hai.",
];

const readingQuestions = [
  {
    question: "What happens in the poem Those Winter Sundays?",
    answer: "Speaker yaad karta hai ke us ka father Sunday ko bhi jaldi uth kar thand me fire jalata tha, shoes polish karta tha aur ghar ko warm banata tha. Bachpan me speaker ne is love ko samjha nahi, magar baad me usay regret hota hai.",
  },
  {
    question: "What is the central idea of the poem?",
    answer: "Central idea yeh hai ke real love hamesha loud ya expressive nahi hota. Bohat dafa love sacrifice, duty aur silent care ke zariye dikhaya jata hai, jise log der se samajhte hain.",
  },
  {
    question: "How is the father presented in the poem?",
    answer: "Father hardworking, responsible, loving aur selfless nazar aata hai. Woh appreciation ke baghair bhi apne family ke liye comfort create karta hai.",
  },
  {
    question: "Why does the speaker feel regret?",
    answer: "Speaker ko regret is liye hota hai kyun ke bachpan me us ne father ki sacrifices aur un ke silent love ki value nahi samjhi. Later realization poem ka emotional center ban jati hai.",
  },
  {
    question: "What does the poem suggest about love and duty?",
    answer: "Poem suggest karti hai ke love aur duty aksar ek doosre se jude hote hain. Father ka care affection ke spoken words se kam aur responsible actions se zyada expressed hota hai.",
  },
];

const literaryPoints = [
  {
    label: "Tone",
    detail: "Tone reflective, regretful aur deeply respectful hai. Speaker past ko yaad karte hue emotional maturity ke sath father ki value samajhta hai.",
  },
  {
    label: "Imagery",
    detail: "Blueblack cold, cracked hands, banked fires blaze aur polished shoes jesi images thand, mehnat aur care ko strongly visualize karti hain.",
  },
  {
    label: "Theme",
    detail: "Major theme unrecognized parental love hai. Poem yeh bhi dikhati hai ke children aksar sacrifices ki value baad me samajhte hain.",
  },
  {
    label: "Message",
    detail: "Poem ka message yeh hai ke humein silent care aur everyday sacrifice ko ignore nahi karna chahiye. Real affection bohat dafa actions me hoti hai, words me nahi.",
  },
];

const mcqs = [
  {
    question: "What kind of love is emphasized in the poem?",
    options: [
      { label: "a", text: "loud and openly celebrated love", verdict: "incorrect", reason: "Poem me love spoken affection se zyada actions me dikhaya gaya hai." },
      { label: "b", text: "silent and sacrificial parental love", verdict: "correct", reason: "Father ke quiet actions poem ka emotional center hain." },
      { label: "c", text: "romantic love between equals", verdict: "incorrect", reason: "Poem parental relationship par based hai, romantic relation par nahi." },
      { label: "d", text: "love shown through gifts and money", verdict: "incorrect", reason: "Father care warmth aur service ke through show karta hai, material gifts ke through nahi." },
    ],
  },
  {
    question: "Why does the speaker feel regret?",
    options: [
      { label: "a", text: "because he never received enough gifts", verdict: "incorrect", reason: "Poem me regret gifts ke bare me nahi, understanding ke bare me hai." },
      { label: "b", text: "because he did not value his father's sacrifices in childhood", verdict: "correct", reason: "Speaker later realize karta hai ke us ne silent love ko us waqt appreciate nahi kiya." },
      { label: "c", text: "because the winter weather was too severe", verdict: "incorrect", reason: "Cold setting important hai, lekin regret ka main cause emotional realization hai." },
      { label: "d", text: "because his father stopped speaking to him", verdict: "incorrect", reason: "Poem ka issue emotional distance aur late understanding hai, direct silence dispute nahi." },
    ],
  },
];

const answerBuilding = [
  "Summary me father ke actions aur speaker ke later regret dono mention karo.",
  "Central idea wale answer me silent love, sacrifice aur delayed understanding ke points include karo.",
  "Tone question me reflective aur regretful words use karna strong answer banata hai.",
  "Agar father ke character ka question ho to hardworking, selfless aur caring tino words useful hain.",
];

usePageSeo({
  title: "Those Winter Sundays Notes | 11th Class English Unit 11",
  description: "11th class English Unit 11 poem notes with summary, central idea, tone, imagery, father's silent love and explained MCQs.",
  keywords: "those winter sundays notes, 11th class english unit 11, class 11 poem notes, those winter sundays summary, father love poem notes",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th English", item: "/11th/english/" },
    { name: "Poems", item: "/11th/english/poems/" },
    { name: "Those Winter Sundays", item: "/11th/english/poems/those-winter-sundays/" },
  ],
});
</script>

<template>
  <PageContainer>
    <section class="app-page-stack">
      <header class="app-page-header">
        <p class="app-page-kicker">Unit 11</p>
        <h1 class="app-page-title">Those Winter Sundays</h1>
        <p class="app-page-intro">
          Yeh page poem ke silent fatherly love, sacrifice aur later regret ko exam prep format me explain karta hai.
        </p>
      </header>

      <section class="english-panel-muted">
        <h2 class="app-section-heading">Textbook-Aligned Snapshot</h2>
        <ul class="app-list">
          <li v-for="point in textbookAlignedPoints" :key="point">{{ point }}</li>
        </ul>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Unit 11 Coverage Checklist</h2>
        <ul class="app-list-grid">
          <li v-for="item in chapterCoverage" :key="item" class="english-mini-card-muted">{{ item }}</li>
        </ul>
      </section>

      <section class="english-panel-muted">
        <h2 class="app-section-heading">Quick Revision Points</h2>
        <ul class="app-list">
          <li v-for="item in quickRevisionPoints" :key="item">{{ item }}</li>
        </ul>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Reading and Explanation Answers</h2>
        <div class="mt-4 space-y-4">
          <article
            v-for="item in readingQuestions"
            :key="item.question"
            class="english-inner-card-muted"
          >
            <h3 class="app-title-card">{{ item.question }}</h3>
            <p class="app-rich-text mt-3">{{ item.answer }}</p>
          </article>
        </div>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Literary Points</h2>
        <div class="mt-4 space-y-4">
          <article
            v-for="item in literaryPoints"
            :key="item.label"
            class="english-inner-card-muted"
          >
            <h3 class="app-title-card">{{ item.label }}</h3>
            <p class="app-rich-text mt-3">{{ item.detail }}</p>
          </article>
        </div>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">MCQs with Option Explanation</h2>
        <div class="mt-4 space-y-4">
          <article
            v-for="item in mcqs"
            :key="item.question"
            class="english-inner-card-muted"
          >
            <h3 class="app-title-card">{{ item.question }}</h3>
            <ul class="mt-3 space-y-2 text-sm leading-6 text-slate-700 dark:text-slate-300">
              <li v-for="option in item.options" :key="option.label">
                <span class="font-medium text-slate-900">{{ option.label }}. {{ option.text }}</span>
                <span>{{ option.verdict === "correct" ? " Correct:" : " Not correct:" }} {{ option.reason }}</span>
              </li>
            </ul>
          </article>
        </div>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Answer-Building Notes</h2>
        <ul class="app-list">
          <li v-for="item in answerBuilding" :key="item">{{ item }}</li>
        </ul>
      </section>
    </section>
  </PageContainer>
</template>





